// Content of the info window shown when a truck is clicked on the Live Map
import { PhoneIcon } from '@heroicons/react/16/solid';
// Same open-in-new-tab icon the scheduler's job titles use (ExternalLinkText)
import { ArrowTopRightOnSquareIcon } from '@heroicons/react/20/solid';

import { replaceParamsInRoute } from '@isd/lib/helpers/functions';

import { PopupJobRow } from '@/components/LiveMap/JobSelection';
import { OperatorAvatar } from '@/components/LiveMap/LiveMapMarkers';
import {
  describeActivity,
  formatPhoneNumber,
  getCompassDirection,
  getPhoneHref,
  getSiteDisplayName,
  getStatusLabel,
} from '@/components/LiveMap/liveMapFormat';
import {
  LiveMapJob,
  LiveMapOperator,
  LiveMapSite,
  TruckActivity,
  TruckLocation,
  formatTimeAgo,
  getActiveAssignments,
  getOperatorMarkerStyle,
} from '@/components/LiveMap/liveMapModel';
import { OperatorProfile } from '@/components/LiveMap/useLiveMapData';

import { reactAppOperatorDetailsRoute, reactAppUrl } from '@/env';

/**
 * Operator, contact, live status and active jobs for one truck.
 * @param {object} props
 * @param {LiveMapOperator} props.operator
 * @param {TruckLocation} props.location
 * @param {OperatorProfile | undefined} props.profile - Avatar, tracking switch and phone; undefined while loading.
 * @param {TruckActivity} props.activity
 * @param {{ site: LiveMapSite, leftAt: number | null } | null} props.cameFrom - Last site visited, from today's route.
 * @param {Record<string, LiveMapJob>} props.jobsById
 * @param {string[]} props.pinnedJobIds
 * @param {(jobId: string) => void} props.onTogglePin - Pin/unpin a job, same as the pane.
 * @param {boolean} props.isStale
 * @param {number} props.nowMs
 * @returns {React.JSX.Element}
 */
export default function TruckDetails({
  operator,
  location,
  profile,
  activity,
  cameFrom,
  jobsById,
  pinnedJobIds,
  onTogglePin,
  isStale,
  nowMs,
}: {
  operator: LiveMapOperator;
  location: TruckLocation;
  profile: OperatorProfile | undefined;
  activity: TruckActivity;
  cameFrom: { site: LiveMapSite; leftAt: number | null } | null;
  jobsById: Record<string, LiveMapJob>;
  pinnedJobIds: string[];
  onTogglePin: (jobId: string) => void;
  isStale: boolean;
  nowMs: number;
}) {
  const phoneNumber = operator.isSimulated
    ? null
    : profile === undefined
      ? undefined
      : profile.phoneNumber;
  const activeAssignments = getActiveAssignments(operator);
  const operatorUrl = `${reactAppUrl}${replaceParamsInRoute(
    reactAppOperatorDetailsRoute,
    { operatorId: operator.uid }
  )}`;
  const showCameFrom =
    !!cameFrom &&
    !(activity.kind === 'atSite' && activity.site.id === cameFrom.site.id);

  return (
    <div className="truck-details text-base-content w-64 space-y-2 text-sm">
      <div className="flex items-start gap-2.5">
        <OperatorAvatar
          operator={operator}
          avatarUrl={profile?.avatarUrl}
          className="size-12 shrink-0 border-2 text-base"
          style={{
            borderColor: getOperatorMarkerStyle({
              lastFixMs: location.latest.timestamp,
              isTracking: operator.isSimulated ? true : profile?.isTracking,
              nowMs,
            }).strokeColor,
          }}
        />
        <div className="min-w-0">
          <div className="text-xs font-bold tracking-wide uppercase opacity-60">
            {operator.truckDisplayId
              ? `Truck ${operator.truckDisplayId}`
              : 'Truck'}
            {operator.isSimulated && ' · Simulated'}
          </div>
          {operator.isSimulated ? (
            <div className="text-base font-bold">{operator.name}</div>
          ) : (
            <a
              href={operatorUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="link link-hover inline-flex items-center gap-1 text-base font-bold"
              title="Open operator profile in a new tab"
            >
              {operator.name}
              <ArrowTopRightOnSquareIcon
                className="text-accent size-4 shrink-0"
                aria-hidden="true"
              />
            </a>
          )}
          {!!operator.companyName && (
            <div className="text-xs opacity-70">{operator.companyName}</div>
          )}
          {phoneNumber === undefined ? (
            <div className="skeleton bg-base-200 mt-1 h-4 w-28" />
          ) : phoneNumber ? (
            <a
              href={getPhoneHref(phoneNumber)}
              className="link link-accent mt-1 inline-flex items-center gap-1"
            >
              <PhoneIcon className="size-3.5" />
              {formatPhoneNumber(phoneNumber)}
            </a>
          ) : (
            <div className="mt-1 text-xs opacity-60">{'No phone number'}</div>
          )}
          {!operator.isSimulated &&
            !!profile &&
            profile.isTracking !== true && (
              <div className="text-warning mt-1 text-xs font-semibold">
                {'Location sharing is off'}
              </div>
            )}
        </div>
      </div>

      <div className="bg-base-200 rounded-md px-2 py-1.5">
        <div className="font-semibold">{describeActivity(activity)}</div>
        {showCameFrom && cameFrom && (
          <div className="text-xs">
            {`Came from ${getSiteDisplayName(cameFrom.site)}`}
            {!!cameFrom.leftAt &&
              `, left ${formatTimeAgo(cameFrom.leftAt, nowMs)}`}
          </div>
        )}
        <div className="text-xs opacity-70">
          {isStale ? 'Last location ' : 'Updated '}
          {formatTimeAgo(location.latest.timestamp, nowMs)}
          {!isStale &&
            location.heading !== null &&
            ` · heading ${getCompassDirection(location.heading)}`}
        </div>
      </div>

      <div>
        <div className="mb-1 text-xs font-bold tracking-wide uppercase opacity-60">
          {activeAssignments.length === 1 ? 'Active job' : 'Active jobs'}
        </div>
        <ul className="space-y-0.5">
          {activeAssignments.map((assignment) => {
            const job = jobsById[assignment.jobId];
            return (
              <li key={assignment.assignmentId}>
                <PopupJobRow
                  jobId={assignment.jobId}
                  job={job}
                  subtitle={[getStatusLabel(assignment.status), job?.clientName]
                    .filter(Boolean)
                    .join(' · ')}
                  isPinned={pinnedJobIds.includes(assignment.jobId)}
                  onTogglePin={onTogglePin}
                />
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
}
